import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import {
    SET_LOGGED_IN,
    SetLoggedIn, UserInfoLoaded,
    SetUserProfile,
    InitAuth,
    INIT_AUTH, USER_INFO_LOADED,
    LOAD_USER_INFO,
    LoadUserInfo,
    SET_AUTH_TOKEN,
    SetAuthToken,
    ClearAllAuthData,
    CLEAR_ALL_AUTH_DATA,
    ExistingLoginFound,
    EXISTING_LOGIN_FOUND,
    TOKEN_EXPIRED,
    TokenExpired,
    ClearAllAuthDataAndThrowError,
    CLEAR_ALL_AUTH_DATA_AND_THROW_ERROR,
    EMAIL_VERIFICATION_REQUIRED,
    EmailVerificationRequired,
    LOGIN_ATTEMPT,
    LoginAttempt,
    SetLoginError,
    ClearLoginError
} from './auth.actions';
import { CartProvider } from '../../core/cart-provider';
import * as Sentry from '@sentry/angular';

import { environment } from '../../../environments/environment';
import {
    catchError,
    filter,
    map,
    retry,
    switchMap,
    take,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { Action, Store } from '@ngrx/store';
import { UpdateCart } from '../cart/cart.actions';
import * as fromApp from '../app.reducers';
import { CartNode } from '../../model/cart.model';
import { AuthService } from '@app/services/auth.service';
import { of, throwError } from 'rxjs';
import {
    AuthConstants,
    AuthErrorMessages
} from '@app/constants/application-constants';
import { ContactService } from '@app/services/contact.service';
import {
    getAuthToken,
    getLoggedInContact,
    getProfile
} from '../app.reducers';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Router } from '@angular/router';

@Injectable()
export class AuthEffects implements OnInitEffects {

    private readonly userProfileMaxRetries = 3;
    private readonly userProfileRetryDelay = 5000;

    initAuth$ = createEffect(() => this.actions$.pipe(
        ofType(INIT_AUTH),
        withLatestFrom(
            this.store.select(getAuthToken),
            this.store.select(getLoggedInContact)
        ),
        map(([action, token, loggedInContact]) => {
            if (token && loggedInContact) {
                if (this.authService.isAuthenticated()) {
                    return new ExistingLoginFound();
                } else {
                    // The user has a token, but it is not valid, so clear all auth data, and
                    // ensure that cart data is cleared as well
                    return new ClearAllAuthData(true);
                }
            }

            // Clear any existing auth data, but do not force a cart clear, to ensure that
            // the logged out user's cart remains intact on page refresh
            return new ClearAllAuthData();
        })
    ));


    reloadCartOnLoggedInUpdate = createEffect(() => this.actions$.pipe(
        ofType(SET_LOGGED_IN),
        tap((action: SetLoggedIn) => {
            // reload cart everytime the user logs out or logs in.

            // if the user logouts, get a new cart
            if (action.clearCart) {
                window.localStorage.removeItem(environment.cartId);
                this.cartProvider
                    .createCart()
                    .pipe(
                        take(1),
                        tap((cart: CartNode) =>
                            window.localStorage.setItem(environment.cartId, cart.id)
                        ),
                        tap((cart: CartNode) => this.store.dispatch(new UpdateCart(cart))),
                        catchError((e) => [e])
                    )
                    .subscribe();
                return;
            }

            this.cartProvider.load();
        })
    ), { dispatch: false });


    existingLoginFound$ = createEffect(() => this.actions$.pipe(
        ofType(EXISTING_LOGIN_FOUND),
        withLatestFrom(this.store.select(getAuthToken)),
        map(([action, authToken]) => new LoadUserInfo(authToken))
    ));


    setAuthToken$ = createEffect(() => this.actions$.pipe(
        ofType(SET_AUTH_TOKEN),
        map((action: SetAuthToken) => new LoadUserInfo(action.authToken, true))
    ));


    userInfoLoaded$ = createEffect(() => this.actions$.pipe(
        ofType(USER_INFO_LOADED),
        map((action: UserInfoLoaded) => {
            this.authService.userProfile$.next(action.profile);
            localStorage.setItem(
                AuthConstants.PROFILE_LOCAL_STORAGE_KEY,
                JSON.stringify(action.profile)
            );
            localStorage.setItem(
                AuthConstants.LOGGED_IN_CONTACT_LOCAL_STORAGE_KEY,
                JSON.stringify(action.contact)
            );

            return new SetLoggedIn(true, false, action.initialLoad);
        })
    ));


    userInfoLoadedSetProfile$ = createEffect(() => this.actions$.pipe(
        ofType(USER_INFO_LOADED),
        map((action: UserInfoLoaded) => new SetUserProfile(action.profile))
    ));


    setLoggedInRedirect$ = createEffect(() => this.actions$.pipe(
        ofType(SET_LOGGED_IN),
        filter((action: SetLoggedIn) => action.redirect),
        tap(async() => {
            this.router.navigateByUrl(this.authService.getAuthenticationURLEntry());
            this.authService.handleAuthenticationURLEntry();
        })
    ), { dispatch: false });


    loadUserInfo$ = createEffect(() => this.actions$.pipe(
        ofType(LOAD_USER_INFO),
        switchMap((action: LoadUserInfo) =>
            this.contactService.getContact(action.authToken?.accessToken).pipe(
                retry({ delay: this.userProfileRetryDelay, count: this.userProfileMaxRetries }),
                tap(contact => {
                    if (!contact.inUse) {
                        throw new Error(AuthErrorMessages.CONTACT_NOT_IN_USE_ERROR);
                    }
                }),
                map(contact => {
                    const token = action.authToken ?? AuthService.getAuthTokenFromLocalStorage();
                    const email = (contact as any).email ?? (contact as any).primaryEmail ?? '';
                    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim() || email;

                    const profile = {
                        email,
                        sub: token?.accessToken,
                        name,
                        nickname: email ? email.split('@')[0] : name,
                        picture: '',
                        updated_at: new Date(),
                        email_verified: true, // Maintained for backwards compatibility. User gets an error during login if not verified.
                        'http://api.hill-laboratories.co.nz/userCrmContactId': ''
                    };

                    return new UserInfoLoaded(profile, contact, action.initialLoad);
                }),
                catchError((error) => {
                    console.error('Profile fetch failed after retries (LOAD_USER_INFO):', error);
                    return of(new SetLoginError('Unable to load your profile. Please try logging in again.'));
                })
            )
        )
    ));


    clearAllAuthData$ = createEffect(() => this.actions$.pipe(
        ofType(CLEAR_ALL_AUTH_DATA),
        map((action: ClearAllAuthData) => {
            this.authService.clearLocalStorageValues();
            this.authService.clearContactDetails({ clearCart: action.clearCart });
        })
    ), { dispatch: false });


    clearLoginErrorOnAttempt$ = createEffect(() => this.actions$.pipe(
        ofType(LOGIN_ATTEMPT),
        map(() => new ClearLoginError())
    ));

    loginAttempt$ = createEffect(() => this.actions$.pipe(
        ofType(LOGIN_ATTEMPT),
        switchMap((action: LoginAttempt) =>
            this.authService.loginWithMyLabAPI(action.email, action.password).pipe(
                map(() => new LoadUserInfo(AuthService.getAuthTokenFromLocalStorage(), !action.isModal)),
                catchError((error) => {
                    if (error.status === 403) {
                        const response = typeof error.response === 'string' ? error.response : '';
                        const verificationMessage = response.toLowerCase();
                        if (verificationMessage.includes('verify your email')) {
                            const isReverification = verificationMessage.includes('verify your email address again');
                            return of(new EmailVerificationRequired(action.email, isReverification));
                        } else {
                            return of(new SetLoginError('Invalid credentials. Please check your email and password.'));
                        }
                    } else if (error.status === 401) {
                        return of(new SetLoginError('Invalid credentials. Please check your email and password.'));
                    } else if (error.error?.message) {
                        return of(new SetLoginError(error.error.message));
                    } else {
                        return of(new SetLoginError('Login failed. Please try again.'));
                    }
                })
            )
        )
    ));

    clearAllAuthDataAndThrowError$ = createEffect(() => this.actions$.pipe(
        ofType(CLEAR_ALL_AUTH_DATA_AND_THROW_ERROR),
        switchMap((e) => {
            const error = e;
            return this.store.select(getProfile).pipe(
                map(profile => ({ error, profile })),
                tap(ep => {
                    Sentry.setContext('user', {
                        email: ep.profile.email,
                    });
                    throwError(ep.error);
                    this.authService.clearLocalStorageValues();
                    this.authService.clearContactDetails();
                })
            );
        })
    ), { dispatch: false });


    tokenExpired$ = createEffect(() => this.actions$.pipe(
        ofType(TOKEN_EXPIRED),
        map((action: TokenExpired) => {
            this.authService.handleAuthenticationURLEntry(
                action.redirectUrl ? action.redirectUrl : this.router.url
            );
            this.authService.logoutAndNavigate('/account/login', true);

            return new ClearAllAuthData();
        })
    ));


    setLoggedInSetSentryUser = createEffect(() => this.actions$.pipe(
        ofType(SET_LOGGED_IN),
        map((action: SetLoggedIn) => action),
        withLatestFrom(this.store.select(getProfile)),
        tap(([action, profile]) => {
            if (action.loggedIn && profile != null) {
                Sentry.setUser({
                    id: profile.sub,
                    email: profile.email,
                });
            } else {
                Sentry.setUser(null);
            }
        })
    ), { dispatch: false });

    emailVerificationRequired$ = createEffect(() => this.actions$.pipe(
        ofType(EMAIL_VERIFICATION_REQUIRED),
        tap((action: EmailVerificationRequired) => {
            this.router.navigate(['/account/verify'], {
                queryParams: {
                    email: action.email,
                    ...(action.isReverification ? { reason: 'reverification' } : {})
                }
            });
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private cartProvider: CartProvider,
        private authService: AuthService,
        private contactService: ContactService,
        private notification: NzNotificationService,
        private router: Router,
        private store: Store<fromApp.AppState>
    ) {}

    ngrxOnInitEffects(): Action {
        return new InitAuth();
    }
}
